/**
 * Form-aware orchestration for appointment slot picking: watches `startTime`, loads policy and slots,
 * autofills the first slot on day selection, and surfaces submit-blocking validation state.
 */

import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import { useGetResourcePolicyQuery } from '@/api/mcp-appointments';
import { useAvailableSlotsSchedule } from './useAvailableSlotsSchedule';
import { useSlotSelectionCore, useSlotSelectionState } from './useSlotSelectionCore';

/** Configuration for wiring slot selection into an Ant Design appointment form. */
interface UseAppointmentSlotSelectionProps {
  /** Form that owns the `startTime` field. */
  form: FormInstance;
  /** Resource whose slots and policy should be loaded. */
  resourceId: string | null | undefined;
  /** Appointment length in minutes; used to derive `endTime` from `startTime`. */
  duration: number;
  /** Whether the hosting dialog or surface is open and should load data. */
  visible: boolean;
  /** Optional calendar day to steer the picker without an initial committed time. */
  preferredDate?: Dayjs;
  /** Whether `startTime` should be treated as already committed when the surface opens. */
  initialCommitted?: boolean;
  /** Slot interval that remains valid even if missing from a fresh availability response. */
  treatAsValidSlot?: { start: Dayjs; end: Dayjs } | null;
}

/**
 * Coordinates slot schedule loading with form state for create, reschedule, and similar flows.
 * @returns Picker handlers, alert inputs, lifecycle helpers, and submit guard flags.
 */
export const useAppointmentSlotSelection = ({
  form,
  resourceId,
  duration,
  visible,
  preferredDate,
  initialCommitted = false,
  treatAsValidSlot = null
}: UseAppointmentSlotSelectionProps) => {
  const state = useSlotSelectionState({ form, duration, initialCommitted });

  const { data: resourcePolicyData, isLoading: isResourcePolicyLoading } = useGetResourcePolicyQuery(
    resourceId || '',
    !!resourceId && visible
  );
  // The policy query resolves to null when the resource has no policy (404); consumers treat that as "no policy".
  const resourcePolicy = resourcePolicyData ?? undefined;

  const schedule = useAvailableSlotsSchedule({
    resourceId,
    startTime: state.startTime,
    endTime: state.endTime,
    resourcePolicy,
    visibleMonth: state.slotsVisibleMonth,
    hasCommittedStartTime: state.hasCommittedStartTime,
    durationMinutes: duration,
    treatAsValidSlot,
    enabled: visible
  });

  const selection = useSlotSelectionCore({
    form,
    visible,
    preferredDate,
    duration,
    identityKeys: [resourceId],
    isSlotsQueryReady: true,
    clearStartTimeOnIdentityChange: false,
    state,
    schedule
  });

  const isLoadingVisibleMonthSlots =
    schedule.isLoadingVisibleMonthSlots || (visible && !!resourceId && isResourcePolicyLoading);

  return { ...selection, resourcePolicy, isLoadingVisibleMonthSlots };
};
